import { useState, useCallback, useEffect, useRef } from 'react'
import { isAxiosError } from 'axios'
import dayjs from 'dayjs'
import {
    StatisticsAvailableDateRange,
    StatisticsDataType,
    StatisticsDeviceOption,
    StatisticsFilters,
    StatisticsPestTypeOption,
    StatisticsProjectOption,
    StatisticsRegionOption,
} from '../../../types/statistics'
import {
    getStatisticsData,
    STATISTICS_REQUEST_TIMEOUT_MS,
} from '../../../services/statisticsService'

export const useStatisticsData = (
    dateRange: [dayjs.Dayjs, dayjs.Dayjs] | null,
    filters: StatisticsFilters,
    enabled: boolean = true,
    refreshKey: number = 0,
    analysisFeature?: string
) => {
    const [loading, setLoading] = useState<boolean>(enabled)
    const [statisticsData, setStatisticsData] =
        useState<StatisticsDataType | null>(null)
    const [availableDateRange, setAvailableDateRange] =
        useState<StatisticsAvailableDateRange | null>(null)
    const [regionOptions, setRegionOptions] = useState<StatisticsRegionOption[]>(
        []
    )
    const [projectOptions, setProjectOptions] = useState<StatisticsProjectOption[]>(
        []
    )
    const [deviceOptions, setDeviceOptions] = useState<StatisticsDeviceOption[]>(
        []
    )
    const [pestTypeOptions, setPestTypeOptions] = useState<
        StatisticsPestTypeOption[]
    >([])
    const [businessDate, setBusinessDate] = useState<string | null>(null)
    const [businessTimezone, setBusinessTimezone] = useState<string | null>(null)
    const [error, setError] = useState<unknown | null>(null)
    const requestIdRef = useRef(0)
    const isMountedRef = useRef(false)
    const abortControllerRef = useRef<AbortController | null>(null)

    useEffect(() => {
        isMountedRef.current = true

        return () => {
            abortControllerRef.current?.abort()
            abortControllerRef.current = null
            isMountedRef.current = false
            requestIdRef.current += 1
        }
    }, [])

    const fetchData = useCallback(async () => {
        void refreshKey
        if (!enabled) {
            abortControllerRef.current?.abort()
            abortControllerRef.current = null
            setLoading(false)
            setBusinessDate(null)
            setBusinessTimezone(null)
            return
        }

        abortControllerRef.current?.abort()
        const abortController = new AbortController()
        abortControllerRef.current = abortController
        const requestId = ++requestIdRef.current
        const isCurrentRequest = () =>
            isMountedRef.current && requestId === requestIdRef.current

        setLoading(true)
        setError(null)

        try {
            const startDate = dateRange?.[0].format('YYYY-MM-DD')
            const endDate = dateRange?.[1].format('YYYY-MM-DD')
            const response = await getStatisticsData(
                startDate,
                endDate,
                filters,
                analysisFeature,
                {
                    signal: abortController.signal,
                    timeout: STATISTICS_REQUEST_TIMEOUT_MS,
                }
            )

            if (!isCurrentRequest()) {
                return
            }

            setStatisticsData({
                ...response.summary,
                speciesSummary: response.speciesSummary,
                availableDateRange: response.availableDateRange ?? null,
            })
            setError(null)
            setAvailableDateRange(response.availableDateRange ?? null)
            setProjectOptions(response.projects)
            setRegionOptions(response.regions)
            setDeviceOptions(response.devices)
            setPestTypeOptions(response.pestTypes)
            setBusinessDate(response.businessDate ?? null)
            setBusinessTimezone(response.businessTimezone ?? null)
        } catch (error) {
            if (!isCurrentRequest()) {
                return
            }

            if (isAxiosError(error) && error.code === 'ERR_CANCELED') {
                return
            }

            console.error('Failed to fetch statistics data:', error)
            if (isAxiosError(error) && error.response?.status === 401) {
                return
            }

            setStatisticsData(null)
            setBusinessDate(null)
            setBusinessTimezone(null)
            setError(error)
        } finally {
            if (abortControllerRef.current === abortController) {
                abortControllerRef.current = null
            }
            if (isCurrentRequest()) {
                setLoading(false)
            }
        }
    }, [analysisFeature, dateRange, enabled, filters, refreshKey])

    useEffect(() => {
        void fetchData()
    }, [fetchData])

    const calculateAvgConfidence = useCallback((): number => {
        return statisticsData?.averageConfidence || 0
    }, [statisticsData])

    return {
        loading,
        statisticsData,
        availableDateRange,
        projectOptions,
        regionOptions,
        deviceOptions,
        pestTypeOptions,
        businessDate,
        businessTimezone,
        error,
        calculateAvgConfidence,
        fetchData,
    }
}
